export const SEGMENT_EVENT_NAME_USER_INTERACTED_WITH_ELEMENT =
  'User interacted with element' as const

export const SEGMENT_EVENT_NAME_USER_VIEWED_SCREEN =
  'User viewed screen' as const

export type SegmentTrackEventName =
  | typeof SEGMENT_EVENT_NAME_USER_INTERACTED_WITH_ELEMENT
  | typeof SEGMENT_EVENT_NAME_USER_VIEWED_SCREEN

/**
 * Property allow-lists copied from the Segment source schema:
 * @see https://app.amplitude.com/data/earnin/Earnin/events/main/latest?view=All&eventsTab=Events&events-all:table-filters=statuses%3Doperator%253Dis%2526type%253DEnum%2526values%25255B0%25255D%253Dlive
 *
 * Note:
 * - We intentionally keep the raw property names (including dotted names).
 * - The CSV contains a blank summary row; it is not included here.
 */
export const USER_INTERACTED_WITH_ELEMENT_ALLOWED_PROPERTY_NAMES = [
  'accountId',
  'accountType',
  'act-e30-accountType',
  'act-e39-accountType',
  'activityAmount',
  'activityPostDate',
  'activityStatus',
  'activityTipAmount',
  'activityType',
  'agreeAllChecked',
  'agreementLinkHypertext',
  'agreementLinkText',
  'agreementLinkURL',
  'agreementsAccepted',
  'amount',
  'answer',
  'autoPay',
  'AutoPay',
  'autoPayChecked',
  'AutopayValue',
  'autoTransferAmt',
  'availableAmount',
  'AvailableAmount',
  'bankName',
  'browserName',
  'browserVersion',
  'buildId',
  'buttonContext',
  'buttonName',
  'campaign',
  'campaignName',
  'canRequestPermission',
  'captchaFor',
  'cardAgreementsAccepted',
  'cardAutoPayChecked',
  'cardBalance',
  'cardStatus',
  'CardStatus',
  'changeToAvailableAmount',
  'choice',
  'Choice',
  'code',
  'commitRef',
  'component',
  'contactInviteStatus',
  'contactType',
  'context',
  'CTAButtonVisibility',
  'currentBalance',
  'DepositAccountTermsChecked',
  'deviceModel',
  'deviceVendor',
  'domainName',
  'earningsSinceAmount',
  'elementName',
  'enrollmentStatus',
  'enteredAmount',
  'eSignPrivacyTermsChecked',
  'failedAttemptsCount',
  'failureReason',
  'feeAmount',
  'fromSecuredAccount',
  'fromStreamedEarnings',
  'hasInteracted',
  'interceptTitle',
  'isAgreementChecked',
  'isAmtWarning',
  'isEmailChanged',
  'isExistingCashoutUser',
  'isFirstNameChanged',
  'isFormValid',
  'isLastNameChanged',
  'isLightningSpeed',
  'isMobile',
  'isPhoneChanged',
  'isReplacementCard',
  'isSecuredAccountOptionAvailable',
  'isToggleOn',
  'isValidPhone',
  'isValidReferralCode',
  'linkClicked',
  'loginHelperSheetEnabled',
  'merchantName',
  'message',
  'nextPaymentDate',
  'option',
  'optionSelected',
  'osName',
  'osVersion',
  'page_number',
  'path',
  'paycheckAmount',
  'payload',
  'paymentAmount',
  'paymentDate',
  'paymentFrom',
  'prizeLinkedEnrollmentStatus',
  'question',
  'reason',
  'referralCode',
  'response',
  'result',
  'scheme',
  'screenContext',
  'screenName',
  'ScreenName',
  'screenPosition',
  'selectedIndex',
  'selectedOption',
  'Selection',
  'selection',
  'shareAction',
  'shownPasswordRequirements',
  'source',
  'statementBalanceAmount',
  'statementDueDate',
  'status',
  'Status',
  'stepName',
  'switchType',
  'taskId',
  'text',
  'tipAmount',
  'tipYourselfStatus',
  'title',
  'toggleType',
  'total_pages',
  'totalAmount',
  'TransactionAmt',
  'TransactionCategory',
  'TransactionPostDate',
  'TransactionStatus',
  'transferAmount',
  'transferTipsAndFeesAmount',
  'type',
  'ua-e22-variant',
  'updateType',
  'upsellType',
  'url',
  'value',
  'variant',
  'variationId',
  'verificationType',
  'viewportHeight',
  'viewportOrientation',
  'viewportWidth',
  'websitePage',
  'WithdrawalAmt',
] as const satisfies ReadonlyArray<string>

export type UserInteractedWithElementPropertyName =
  (typeof USER_INTERACTED_WITH_ELEMENT_ALLOWED_PROPERTY_NAMES)[number]

export const USER_VIEWED_SCREEN_ALLOWED_PROPERTY_NAMES = [
  'accountType',
  'act-e39 variantId',
  'activityAmount',
  'activityFeeAmount',
  'activityPostDate',
  'activityStatus',
  'activityTransactionDate',
  'activityType',
  'addressBookEnabled',
  'addressBookStatus',
  'amount',
  'ATMCashAdvanceDailyLimit',
  'atmPinStatus',
  'attemptedActivationCount',
  'autoPay',
  'AutoPay',
  'AutopayValue',
  'autoTransferAmt',
  'availableAmount',
  'AvailableAmount',
  'availableBalance',
  'AvailableFund',
  'availableLoginType',
  'bankName',
  'banksDisplayed',
  'banner',
  'browserName',
  'browserVersion',
  'buildId',
  'buttonContext',
  'campaignName',
  'canRequestPermission',
  'cardBalance',
  'cardStatus',
  'CardStatus',
  'cash',
  'CashAdvanceFee',
  'changeToAvailableAmount',
  'checkBoxList',
  'code',
  'commitRef',
  'contactListSize',
  'contactSupportButtonEnabled',
  'context',
  'currentBalance',
  'currentPayPeriodAutoTransferAmt',
  'currentPayPeriodMax',
  'currentPayPeriodPaycheck',
  'DailySpentLimit',
  'defaultPayroll',
  'deviceModel',
  'deviceVendor',
  'EarninATMFee',
  'earningsMethodStatus',
  'earningsSinceAmount',
  'earningsSinceDate',
  'earningsSinceDateAmount',
  'earningsSpent',
  'effectiveStarting',
  'elementName',
  'enrollmentStatus',
  'errorMessage',
  'errorReason',
  'exception',
  'existingTransferAmt',
  'experimentGroup',
  'factorType',
  'failedAttemptsCount',
  'filterBy',
  'hasAgreementCheckbox',
  'hasTransactionDetailsToggle',
  'interceptTitle',
  'InterestCharges',
  'isAgreementChecked',
  'isAptSuitePrefilled',
  'isAtomicFlowHidden',
  'isAutoPayUpsellOptionAvailable',
  'isAutoTransferMaxedOut',
  'isConfirmButtonDisabled',
  'isCustomRestoreDate',
  'isDOBPrefilled',
  'isEmailButtonShown',
  'isEmailEdiitable',
  'isEmailPrefilled',
  'isExistingCashoutUser',
  'isFirstNamePrefilled',
  'isHomeAddressPrefilled',
  'isInfoInTiles',
  'isLastNamePrefilled',
  'isMobile',
  'isPhoneEdiitable',
  'isPhonePrefilled',
  'isPifChain',
  'isRepaymentOverdue',
  'isReplacementCard',
  'isSecuredAccountOptionAvailable',
  'isSkipAllowed',
  'isSSNPrefilled',
  'isToggleOn',
  'isWarning',
  'LockCardStatus',
  'lockCardStatus',
  'LockedCardStatus',
  'lockedStatus',
  'nextPaymentDate',
  'notificationStatus',
  'offerId',
  'osName',
  'osVersion',
  'outcomeType',
  'page',
  'page_number',
  'path',
  'paycheckAmount',
  'paymentAmount',
  'paymentDate',
  'paymentFrom',
  'payNowButton',
  'previousScreen',
  'prizeLinkedEnrollmentStatus',
  'reason',
  'Reason',
  'repaymentAmount',
  'repaymentDate',
  'response',
  'screenContext',
  'screenName',
  'screenPosition',
  'section',
  'selectedOption',
  'SendTo',
  'signupMethod',
  'source',
  'state',
  'statementBalanceAmount',
  'statementDueDate',
  'status',
  'stepName',
  'streamedEarningsAllowed',
  'StreamedEarningsAllowed',
  'switchType',
  'tipYourselfStatus',
  'title',
  'total_pages',
  'TransactionAmt',
  'TransactionCategory',
  'transactionDate',
  'TransactionDate',
  'TransactionPostDate',
  'TransactionStatus',
  'transferTipsAndFeesAmount',
  'ua-e22-variant',
  'updateType',
  'upsellType',
  'url',
  'utmSource',
  'variant',
  'variationId',
  'viewportHeight',
  'viewportOrientation',
  'viewportWidth',
  'warningText',
  'websitePage',
  'WithdrawalAmt',
] as const satisfies ReadonlyArray<string>

export type UserViewedScreenPropertyName =
  (typeof USER_VIEWED_SCREEN_ALLOWED_PROPERTY_NAMES)[number]

export const USER_INTERACTED_WITH_ELEMENT_ALLOWED_PROPERTY_NAMES_SET: ReadonlySet<UserInteractedWithElementPropertyName> =
  new Set(USER_INTERACTED_WITH_ELEMENT_ALLOWED_PROPERTY_NAMES)

export const USER_VIEWED_SCREEN_ALLOWED_PROPERTY_NAMES_SET: ReadonlySet<UserViewedScreenPropertyName> =
  new Set(USER_VIEWED_SCREEN_ALLOWED_PROPERTY_NAMES)

export const SEGMENT_ALLOWED_PROPERTY_NAMES_SET_BY_EVENT: Record<
  SegmentTrackEventName,
  ReadonlySet<string>
> = {
  [SEGMENT_EVENT_NAME_USER_INTERACTED_WITH_ELEMENT]:
    USER_INTERACTED_WITH_ELEMENT_ALLOWED_PROPERTY_NAMES_SET,
  [SEGMENT_EVENT_NAME_USER_VIEWED_SCREEN]:
    USER_VIEWED_SCREEN_ALLOWED_PROPERTY_NAMES_SET,
}

export const pickAllowedSegmentProperties = (
  eventName: SegmentTrackEventName,
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  if (!properties) return properties

  const allowed = SEGMENT_ALLOWED_PROPERTY_NAMES_SET_BY_EVENT[eventName]
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(properties)) {
    if (allowed.has(key)) {
      result[key] = value
    }
  }

  return result
}
